import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../config/api';
import { RootState } from '../config/store';

// --- TYPE DEFINITIONS ---

interface ErrorType {
  message: string;
}

interface Client {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email: string;
  phoneNumber?: string;
  address: {
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  status: number;
  source?: string;
  active: boolean;
  notes?: Note[];
}

interface Note {
  userId: string;
  createdAt: string;
  text: string;
  attachments: { description: string; url: string }[];
  file: string
}

interface NotesPayload {
  id: string;
  notes: Note[];
}

interface fetchClientsPayload {
  pageSize?: number;
  page?: number;
  sortColumn?: string;
  sortOrder?: 'ASC' | 'DESC';
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phoneNumber?: string; // Changed from 'phone' and type to string
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  source?: string;
  status?: number;
  // You can add more filters here as needed.
}


// --- ASYNC THUNKS (SIMPLIFIED) ---

export const fetchClients = createAsyncThunk<Client[], fetchClientsPayload>(
  'clients/fetchClients',
  async (args: fetchClientsPayload, { rejectWithValue }) => {
    try {
      const params: Record<string, string> = {};

      for (const key of Object.keys(args as object) as Array<keyof fetchClientsPayload>) {
        const value = args[key];
        
        // This is the updated condition. It now also checks for empty strings.
        if (value !== undefined && value !== null && value !== '') {
          params[key] = String(value);
        }
      }

      const query = new URLSearchParams(params).toString();
      
      const response = await api.get(`/Clients?${query}`);
      
      return response.data.items || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);


export const fetchClientsOrder = createAsyncThunk<Client[], void>(
  'clients/fetchClientsOrder',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/Clients?pageSize=1000`);
      return response.data.items || response.data;
    } catch (error: any)      {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchClientById = createAsyncThunk<Client, string>(
  'clients/fetchClientById',
  async (id, { rejectWithValue }) => {
    if (!id) {
      // Reject if the ID is not provided
      return rejectWithValue('Client ID is required');
    }

    try {
      const response = await api.get(`/Clients/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch client');
    }
  }
);

export const createClient = createAsyncThunk<Client, Partial<Client>>(
  'clients/createClient',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await api.post('/Clients', clientData);
      localStorage.setItem('clientId', JSON.stringify(response.data));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateClient = createAsyncThunk<Client, Partial<Client> & { id: string }>(
  'clients/updateClient',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await api.put(`/Clients/${clientData.id}`, clientData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateClientNotes = createAsyncThunk<Client, NotesPayload>(
  'clients/updateClientNotes',
  async (notesData, { rejectWithValue }) => {
    try {
      const response = await api.put(`/Clients/${notesData.id}/Notes`, notesData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

// Returns the ID of the deleted client
export const deleteClient = createAsyncThunk<string, string>(
  'clients/deleteClient',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/Clients/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

// --- SLICE DEFINITION ---

interface ClientState {
  items: Client[];
  notes: Note[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed'; // Legacy
  listStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  detailsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  saveStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: ErrorType | null;
  clientById: Client | null;
}

const initialState: ClientState = {
  items: [],
  notes: [],
  status: 'idle',
  listStatus: 'idle',
  detailsStatus: 'idle',
  saveStatus: 'idle',
  error: null,
  clientById: null,
};

const clientSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    resetClientData: (state) => {
      state.clientById = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.listStatus = 'loading';
        state.status = 'loading';
      })
      .addCase(fetchClients.fulfilled, (state, action: PayloadAction<Client[]>) => {
        state.listStatus = 'succeeded';
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.status = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(fetchClientsOrder.pending, (state) => {
        state.listStatus = 'loading';
        state.status = 'loading';
      })
      .addCase(fetchClientsOrder.fulfilled, (state, action: PayloadAction<Client[]>) => {
        state.listStatus = 'succeeded';
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchClientsOrder.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.status = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(fetchClientById.pending, (state) => {
        state.detailsStatus = 'loading';
        // state.status = 'loading'; // Avoid global spinner
      })
      .addCase(fetchClientById.fulfilled, (state, action: PayloadAction<Client>) => {
        state.detailsStatus = 'succeeded';
        state.status = 'succeeded';
        state.clientById = action.payload;
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        state.detailsStatus = 'failed';
        state.status = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(createClient.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(createClient.fulfilled, (state, action: PayloadAction<Client>) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.items.push(action.payload); // Add new client to the list
      })
      .addCase(createClient.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(updateClient.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateClient.fulfilled, (state, action: PayloadAction<Client>) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        // Find and update the client in the list
        const index = state.items.findIndex(client => client.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.clientById = action.payload; // Also update the detailed view if it's the same client
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(deleteClient.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(deleteClient.fulfilled, (state, action: PayloadAction<string>) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        // Remove the client from the list
        state.items = state.items.filter(client => client.id !== action.payload);
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(updateClientNotes.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateClientNotes.fulfilled, (state, action: PayloadAction<Client>) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        // Update notes for the specific client if needed
        const index = state.items.findIndex(client => client.id === action.payload.id);
        if (index !== -1) {
          state.items[index].notes = action.payload.notes;
        }
      })
      .addCase(updateClientNotes.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.payload as ErrorType;
      });
  }
});

export const { resetClientData } = clientSlice.actions;

export default clientSlice.reducer;